/**
 * Natural Language Parser Utility
 * 
 * Converts natural language text into structured JSON format
 * for the grading system.
 */

class NaturalLanguageParser {
  constructor() {
    this.patterns = {
      question: [
        /^(\d+)[\.\)]\s*(.+)/i,
        /^Question\s*(\d+):?\s*(.+)/i,
        /^Q\.?\s*(\d+):?\s*(.+)/i,
        /^(\d+)\.\s*(.+)/i
      ],
      answer: [
        /^Answer:\s*(.+)/i,
        /^Correct\s+Answer:\s*(.+)/i,
        /^Solution:\s*(.+)/i,
        /^Ans\.?\s*(.+)/i
      ],
      score: [
        /^Score:\s*(\d+)/i,
        /^Points:\s*(\d+)/i,
        /^Max\s+Score:\s*(\d+)/i,
        /^(\d+)\s+points/i
      ],
      rubric: [
        /^Rubric:\s*(.+)/i,
        /^Grading\s+Criteria:\s*(.+)/i,
        /^Scoring:\s*(.+)/i
      ],
      student: [
        /^Student:\s*(.+)/i,
        /^Name:\s*(.+)/i,
        /^Submitted\s+by:\s*(.+)/i
      ],
      studentAnswer: [
        /^Answer\s+to\s+Q\.?\s*(\d+):?\s*(.+)/i,
        /^Q\.?\s*(\d+)\s+Answer:\s*(.+)/i,
        /^(\d+)[\.\)]\s*(.+)/i
      ]
    };
  }

  /**
   * Parse natural language text into structured answer key
   * @param {string} text - Natural language text
   * @returns {Object} - Structured answer key
   */
  parseAnswerKey(text) {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    const questions = [];
    let currentQuestion = null;
    let currentSection = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check for question
      const questionMatch = this.matchPattern(line, this.patterns.question);
      if (questionMatch) {
        // Save previous question
        if (currentQuestion) {
          this.finalizeQuestion(currentQuestion);
          questions.push(currentQuestion);
        }

        // Start new question
        currentQuestion = {
          questionNumber: questionMatch[1],
          question: questionMatch[2],
          correctAnswer: '',
          maxScore: 10,
          rubric: { accuracy: 40, completeness: 30, clarity: 20, originality: 10 }
        };
        currentSection = 'question';
        continue;
      }

      // Check for answer
      const answerMatch = this.matchPattern(line, this.patterns.answer);
      if (answerMatch && currentQuestion) {
        currentQuestion.correctAnswer = answerMatch[1];
        currentSection = 'answer';
        continue;
      }

      // Check for score
      const scoreMatch = this.matchPattern(line, this.patterns.score);
      if (scoreMatch && currentQuestion) {
        currentQuestion.maxScore = parseInt(scoreMatch[1]);
        continue;
      }

      // Check for rubric
      const rubricMatch = this.matchPattern(line, this.patterns.rubric);
      if (rubricMatch && currentQuestion) {
        currentQuestion.rubric = this.parseRubric(rubricMatch[1]);
        continue;
      }

      // Accumulate text based on current section
      if (currentQuestion) {
        if (currentSection === 'question') {
          currentQuestion.question += ' ' + line;
        } else if (currentSection === 'answer') {
          currentQuestion.correctAnswer += ' ' + line;
        }
      }
    }

    // Add last question
    if (currentQuestion) {
      this.finalizeQuestion(currentQuestion);
      questions.push(currentQuestion);
    }

    return { questions };
  }

  /**
   * Parse natural language text into structured student work
   * @param {string} text - Natural language text
   * @returns {Object} - Structured student work
   */
  parseStudentWork(text) {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    const answers = [];
    let studentName = 'Anonymous';
    let currentAnswer = null;

    // Extract student name
    for (const line of lines) {
      const studentMatch = this.matchPattern(line, this.patterns.student);
      if (studentMatch) {
        studentName = studentMatch[1];
        break;
      }
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check for student answer
      const answerMatch = this.matchPattern(line, this.patterns.studentAnswer);
      if (answerMatch) {
        // Save previous answer
        if (currentAnswer) {
          answers.push(currentAnswer);
        }

        // Start new answer
        currentAnswer = {
          questionNumber: answerMatch[1],
          answer: answerMatch[2] || ''
        };
        continue;
      }

      // Accumulate answer text
      if (currentAnswer && line && !this.matchPattern(line, this.patterns.student)) {
        currentAnswer.answer += ' ' + line;
      }
    }

    // Add last answer
    if (currentAnswer) {
      answers.push(currentAnswer);
    }

    return {
      studentName,
      answers
    };
  }

  /**
   * Parse rubric from natural language
   * @param {string} rubricText - Rubric text
   * @returns {Object} - Structured rubric
   */
  parseRubric(rubricText) {
    const rubric = {};
    const parts = rubricText.split(/[,;]/).map(part => part.trim());

    for (const part of parts) {
      const match = part.match(/(\w+)\s*\((\d+)%\)/i);
      if (match) {
        const criterion = match[1].toLowerCase();
        const weight = parseInt(match[2]);
        rubric[criterion] = weight;
      }
    }

    // Ensure we have at least some rubric
    if (Object.keys(rubric).length === 0) {
      return { accuracy: 40, completeness: 30, clarity: 20, originality: 10 };
    }

    return rubric;
  }

  /**
   * Finalize question by cleaning up text
   * @param {Object} question - Question object
   */
  finalizeQuestion(question) {
    // Clean up text
    question.question = this.cleanText(question.question);
    question.correctAnswer = this.cleanText(question.correctAnswer);

    // Ensure maxScore is valid
    if (isNaN(question.maxScore) || question.maxScore <= 0) {
      question.maxScore = 10;
    }

    // Normalize rubric weights to sum to 100
    this.normalizeRubric(question.rubric);
  }

  /**
   * Normalize rubric weights to sum to 100
   * @param {Object} rubric - Rubric object
   */
  normalizeRubric(rubric) {
    const total = Object.values(rubric).reduce((sum, weight) => sum + weight, 0);
    
    if (total > 0 && total !== 100) {
      for (const criterion in rubric) {
        rubric[criterion] = Math.round((rubric[criterion] / total) * 100);
      }
    }
  }

  /**
   * Clean text by removing extra spaces and normalizing
   * @param {string} text - Text to clean
   * @returns {string} - Cleaned text
   */
  cleanText(text) {
    if (!text) return '';
    
    return text
      .replace(/\s+/g, ' ')
      .replace(/\s+([.,;!?])/g, '$1')
      .trim();
  }

  /**
   * Match line against patterns
   * @param {string} line - Line to match
   * @param {Array} patterns - Array of regex patterns
   * @returns {Array|null} - Match result or null
   */
  matchPattern(line, patterns) {
    for (const pattern of patterns) {
      const match = line.match(pattern);
      if (match) {
        return match;
      }
    }
    return null;
  }

  /**
   * Convert structured data back to natural language
   * @param {Object} data - Structured data
   * @param {string} type - 'answerKey' or 'studentWork'
   * @returns {string} - Natural language text
   */
  toNaturalLanguage(data, type) {
    if (type === 'answerKey') {
      return this.answerKeyToNaturalLanguage(data);
    } else {
      return this.studentWorkToNaturalLanguage(data);
    }
  }

  /**
   * Convert answer key to natural language
   */
  answerKeyToNaturalLanguage(answerKey) {
    let text = '';
    
    answerKey.questions.forEach((question, index) => {
      text += `Question ${question.questionNumber}: ${question.question}\n`;
      text += `Correct Answer: ${question.correctAnswer}\n`;
      text += `Max Score: ${question.maxScore}\n`;
      
      // Format rubric
      const rubricParts = Object.entries(question.rubric || {})
        .map(([criterion, weight]) => `${criterion.charAt(0).toUpperCase() + criterion.slice(1)} (${weight}%)`);
      
      if (rubricParts.length > 0) {
        text += `Rubric: ${rubricParts.join(', ')}\n`;
      }
      
      text += '\n';
    });
    
    return text.trim();
  }

  /**
   * Convert student work to natural language
   */
  studentWorkToNaturalLanguage(studentWork) {
    let text = `Student: ${studentWork.studentName}\n\n`;
    
    studentWork.answers.forEach((answer, index) => {
      text += `Question ${answer.questionNumber} Answer: ${answer.answer}\n\n`;
    });
    
    return text.trim();
  }

  /**
   * Validate if text can be parsed
   * @param {string} text - Text to validate
   * @param {string} type - 'answerKey' or 'studentWork'
   * @returns {boolean} - Whether text is parseable
   */
  validate(text, type) {
    try {
      if (type === 'answerKey') {
        const result = this.parseAnswerKey(text);
        return result.questions.length > 0;
      } else {
        const result = this.parseStudentWork(text);
        return result.answers.length > 0;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Get parsing statistics
   * @param {string} text - Text to analyze
   * @param {string} type - 'answerKey' or 'studentWork'
   * @returns {Object} - Parsing statistics
   */
  getStats(text, type) {
    try {
      let result;
      if (type === 'answerKey') {
        result = this.parseAnswerKey(text);
        return {
          questions: result.questions.length,
          parseable: true,
          avgScore: result.questions.reduce((sum, q) => sum + q.maxScore, 0) / result.questions.length
        };
      } else {
        result = this.parseStudentWork(text);
        return {
          answers: result.answers.length,
          parseable: true,
          studentName: result.studentName
        };
      }
    } catch (error) {
      return {
        parseable: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const naturalLanguageParser = new NaturalLanguageParser();

export default naturalLanguageParser;
